/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import Account, {AccountFields} from '../../../models/accountModel';
import {AccountAdminErrors, DataMode} from '../../../util/interfaces';
import {AdminContext} from '../../misc/adminContext';
import {EventType, navigate, ResponseData, validateEmail} from '@dvrd/dvr-controls';
import AccountDetails from './accountDetails';
import {onAccountEvent} from '../../../util/events';
import {SettingsFields} from '../../../models/settingsModel';
import {ADMIN_USER_ROUTE} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const EMPTY_ERRORS: AccountAdminErrors = {
    zipCode: null,
    telNumber: null,
    companyName: null,
    address: null,
    city: null,
    coc: null,
    email: null,
    country: null
};

export default function AccountDetailsController() {
    const context = useContext(AdminContext);
    const {t} = useTranslation();
    const [account, setAccount] = useState<Account>(new Account({}));
    const [errors, setErrors] = useState<AccountAdminErrors>(EMPTY_ERRORS);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<DataMode>(DataMode.DETAILS);

    function onChange(key: keyof AccountFields & keyof SettingsFields, entity: 'account' | 'settings') {
        return function (value: string) {
            const _account = account.clone();
            if (entity === 'account') _account.setField(key, value);
            else _account.settings = _account.settings.setField(key, value);
            setAccount(_account);
        };
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        account.updateAdmin((account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                const user = context.activeUser;
                if (user) context.onChangeContext({user: user.updateAccount(account)});
                onAccountEvent(account, EventType.UPDATE);
                showSnackbar(t('admin.account.updatedSnack', 'Het account is bijgewerkt'));
                setMode(DataMode.DETAILS);
                setAccount(account);
            } else
                showDialog(
                    data.message ?? t('admin.account.updateFailedMsg', 'Het bijwerken van het account is niet gelukt.'),
                    t('admin.account.updateFailedTitle', 'Bijwerken mislukt')
                );
        });
    }

    function onClose() {
        context.onChangeContext({account: null});
        navigate(ADMIN_USER_ROUTE);
    }

    function onClickDelete() {
        showDialog(
            t('admin.account.deleteConfirmMsg', 'Weet je zeker dat je dit account wilt verwijderen?'),
            t('admin.account.confirmTitle', 'Bevestig'),
            [
                t('admin.account.no', 'Nee'),
                {label: t('admin.account.yes', 'Ja'), onClick: onConfirmDelete, primary: false}
            ]
        );
    }

    function onConfirmDelete() {
        setLoading(true);
        account.deleteAdmin((account: Account, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                showSnackbar(t('admin.account.deletedSnack', 'Het account is verwijderd'));
                onAccountEvent(account, EventType.DELETE);
                onClose();
            } else
                showDialog(
                    data.message ??
                        t('admin.account.deleteFailedMsg', 'Het verwijderen van het account is niet gelukt.'),
                    t('admin.account.deleteFailedTitle', 'Verwijderen mislukt')
                );
        });
    }

    function onResetError(key: keyof AccountAdminErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onChangeMode(mode: DataMode) {
        return function () {
            setMode(mode);
        };
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = Object.assign({}, errors);
        if (!account.companyName) {
            valid = false;
            _errors.companyName = t('admin.account.validation.companyName', 'De bedrijfsnaam mag niet leeg zijn');
        }
        if (!account.address) {
            valid = false;
            _errors.address = t('admin.account.validation.address', 'Het adres mag niet leeg zijn');
        }
        if (!account.zipCode) {
            valid = false;
            _errors.zipCode = t('admin.account.validation.zipCode', 'De postcode mag niet leeg zijn');
        }
        if (!account.city) {
            valid = false;
            _errors.city = t('admin.account.validation.city', 'De stad mag niet leeg zijn');
        }
        if (!account.country) {
            valid = false;
            _errors.country = t('admin.account.validation.country', 'Het land mag niet leeg zijn');
        }
        if (!account.email) {
            valid = false;
            _errors.email = t('admin.account.validation.emailEmpty', 'Het e-mailadres mag niet leeg zijn');
        } else if (!validateEmail(account.email)) {
            valid = false;
            _errors.email = t('admin.account.validation.emailInvalid', 'Het e-mailadres is niet geldig');
        }
        if (!account.telNumber) {
            valid = false;
            _errors.telNumber = t('admin.account.validation.telNumber', 'Het telefoonnummer mag niet leeg zijn');
        }
        if (!account.cocNumber) {
            valid = false;
            _errors.coc = t('admin.account.validation.coc', 'Het KvK-nummer mag niet leeg zijn');
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        const account = context.activeAccount;
        if (account) setAccount(account.clone());
        else history.back();
    }, []);

    return (
        <AccountDetails
            onChange={onChange}
            onSubmit={onSubmit}
            onClose={onClose}
            onClickDelete={onClickDelete}
            onResetError={onResetError}
            onChangeMode={onChangeMode}
            mode={mode}
            errors={errors}
            account={account}
            loading={loading}
        />
    );
}
